// src/modules/app-views/_apps/appsindico/units/pages/Document/tabs/VehiclesTab.jsx
// *******************************************************************************
// VEHICLES TAB
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect } from 'react';
import {
  Table,
  Button,
  Flex,
  Popconfirm,
  Modal,
  Form,
  Select,
  Input,
  Checkbox,
  Divider,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
  CarOutlined,
} from '@ant-design/icons';
import { useDocumentContext } from 'components/core/DocumentProvider';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const VEHICLE_TYPE_OPTIONS = [
  { value: 'car', label: translateX('car') },
  { value: 'motorcycle', label: translateX('motorcycle') },
  { value: 'truck', label: translateX('truck') },
  { value: 'van', label: translateX('van') },
  { value: 'other', label: translateX('other') },
];

const PLATE_MAX_LENGTH = 7;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const VehiclesTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, _id, refresh } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [plateFound, setPlateFound] = useState(false);
  const [skipPlate, setSkipPlate] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadVehicles();
  }, [data?.vehicleIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const loadVehicles = useCallback(async () => {
    if (!data?.vehicleIds?.length) {
      setVehicles([]);
      return;
    }

    setVehiclesLoading(true);
    try {
      const promises = data.vehicleIds.map(id =>
        axiosService.get({ url: `/v1/vehicles/${id}` }).catch(() => [null])
      );
      const results = await Promise.all(promises);
      setVehicles(results.map(r => r?.[0]).filter(Boolean));
    } catch {
      setVehicles([]);
    } finally {
      setVehiclesLoading(false);
    }
  }, [data?.vehicleIds]);

  const handlePlateSearch = useCallback(async () => {
    const plateValue = form.getFieldValue('plate');
    if (!plateValue || plateValue.length < PLATE_MAX_LENGTH) {
      appService.message({ flag: 'w', text: 'plate_must_have_7_chars' });
      return;
    }

    setSearching(true);
    setPlateFound(false);

    try {
      const cleanPlate = plateValue.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      const [response] = await axiosService.post({
        url: '/v1/data-search/plate',
        data: { plate: cleanPlate },
      });

      if (response?.success && response?.data) {
        const vehicleData = response.data;
        form.setFieldsValue({
          brand: vehicleData.brand || '',
          model: vehicleData.model || '',
          year: vehicleData.yearModel || vehicleData.year || '',
          color: vehicleData.color || '',
          fuel: vehicleData.fuel || '',
          type: mapSegmentToType(vehicleData.segment || vehicleData.vehicleType),
        });
        setPlateFound(true);
        appService.message({ flag: 's', text: 'plate_found' });
      } else {
        appService.message({ flag: 'w', text: 'plate_not_found' });
        setPlateFound(false);
      }
    } catch {
      appService.message({ flag: 'w', text: 'plate_search_error' });
      setPlateFound(false);
    } finally {
      setSearching(false);
    }
  }, [form]);

  const handlePlateChange = useCallback(
    e => {
      const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      form.setFieldValue('plate', value);

      // Auto-search when plate reaches max length
      if (value.length === PLATE_MAX_LENGTH && !skipPlate) {
        setTimeout(() => handlePlateSearch(), 100);
      }
    },
    [form, skipPlate, handlePlateSearch]
  );

  const handleAdd = useCallback(() => {
    form.resetFields();
    setPlateFound(false);
    setSkipPlate(false);
    setEditingVehicle(null);
    setShowModal(true);
  }, [form]);

  const handleEdit = useCallback(
    record => {
      form.setFieldsValue({
        type: record.type || 'car',
        brand: record.brand || '',
        model: record.model || '',
        year: record.year || '',
        color: record.color || '',
        plate: record.plate || '',
        fuel: record.fuel || '',
        tagNumber: record.tagNumber || '',
        observation: record.observation || '',
      });
      setPlateFound(false);
      setSkipPlate(true);
      setEditingVehicle(record);
      setShowModal(true);
    },
    [form]
  );

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const vehiclePayload = {
        type: values.type || 'car',
        brand: values.brand || '',
        model: values.model || '',
        year: values.year || '',
        color: values.color || '',
        plate: (values.plate || '').toUpperCase(),
        fuel: values.fuel || '',
        tagNumber: values.tagNumber || '',
        observation: values.observation || '',
        unitId: _id,
      };

      if (editingVehicle?._id) {
        // Update existing vehicle
        await axiosService.put({
          url: `/v1/vehicles/${editingVehicle._id}`,
          data: vehiclePayload,
        });
      } else {
        // Create new vehicle document
        const [vehicle] = await axiosService.post({
          url: '/v1/vehicles',
          data: vehiclePayload,
        });

        if (vehicle?._id) {
          const updatedIds = [...(data?.vehicleIds || []), vehicle._id];
          await axiosService.put({
            url: `/v1/units/${_id}`,
            data: { vehicleIds: updatedIds },
          });
        }
      }

      refresh?.();
      setShowModal(false);
      appService.message({ flag: 's', text: 'saved_successfully' });
    } catch (error) {
      if (!error?.errorFields) {
        appService.message({ flag: 'e', text: 'error_saving' });
      }
    } finally {
      setLoading(false);
    }
  }, [form, editingVehicle, data?.vehicleIds, _id, refresh]);

  const handleRemove = useCallback(
    async vehicleId => {
      setLoading(true);
      try {
        // Remove from unit's vehicleIds
        const updatedIds = (data?.vehicleIds || []).filter(id => id !== vehicleId);
        await axiosService.put({
          url: `/v1/units/${_id}`,
          data: { vehicleIds: updatedIds },
        });

        // Delete vehicle document
        await axiosService.delete({
          url: '/v1/vehicles',
          _id: vehicleId,
        });

        refresh?.();
        appService.message({ flag: 's', text: 'removed_successfully' });
      } catch {
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [data?.vehicleIds, _id, refresh]
  );

  const handleModalCancel = useCallback(() => {
    setShowModal(false);
    setEditingVehicle(null);
  }, []);

  const handleSkipPlateChange = useCallback(
    e => {
      setSkipPlate(e.target.checked);
      if (e.target.checked) {
        setPlateFound(false);
      }
    },
    []
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('type'),
      dataIndex: ['type'],
      width: 60,
      align: 'center',
      render: type => <CarOutlined title={translateX(type || 'car')} />,
    },
    {
      title: translateX('vehicle'),
      dataIndex: ['brand'],
      render: (brand, record) => {
        const label = [brand, record.model].filter(Boolean).join(' ');
        return label || '-';
      },
    },
    {
      title: translateX('plate'),
      dataIndex: ['plate'],
      width: 120,
      render: plate =>
        plate ? (
          <Tag style={{ fontFamily: 'monospace', letterSpacing: 1 }}>
            {plate.toUpperCase()}
          </Tag>
        ) : (
          '-'
        ),
    },
    {
      title: translateX('color'),
      dataIndex: ['color'],
      width: 100,
      render: color => color || '-',
    },
    {
      title: translateX('year'),
      dataIndex: ['year'],
      width: 80,
      render: year => year || '-',
    },
    {
      title: translateX('actions'),
      width: 90,
      align: 'center',
      render: (_, record) => (
        <Flex gap={4} justify="center">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleRemove(record._id)}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={loading}
            />
          </Popconfirm>
        </Flex>
      ),
    },
  ];

  const modalTitle = editingVehicle ? translateX('edit_vehicle') : translateX('add_vehicle');

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={vehicles}
        pagination={false}
        loading={vehiclesLoading || loading}
        size="small"
      />

      <Flex justify="end" className="mt-3">
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_vehicle')}
        </Button>
      </Flex>

      <Modal
        title={modalTitle}
        open={showModal}
        onCancel={handleModalCancel}
        onOk={handleSave}
        confirmLoading={loading}
        destroyOnHidden
        width={520}
      >
        <Form form={form} layout="vertical" className="mt-3">

          {/* Plate search section */}
          {!editingVehicle && (
            <>
              <Divider orientation="left" style={{ fontSize: 13 }}>
                {translateX('plate_search')}
              </Divider>

              <Form.Item name="plate" label={translateX('plate')}>
                <Input
                  maxLength={PLATE_MAX_LENGTH}
                  placeholder="ABC1234"
                  style={{ textTransform: 'uppercase', letterSpacing: 2 }}
                  onChange={handlePlateChange}
                  suffix={
                    <Button
                      type="text"
                      size="small"
                      icon={<SearchOutlined />}
                      loading={searching}
                      onClick={handlePlateSearch}
                      disabled={skipPlate}
                      style={{ marginRight: -4 }}
                    />
                  }
                />
              </Form.Item>

              <Form.Item>
                <Checkbox checked={skipPlate} onChange={handleSkipPlateChange}>
                  {translateX('dont_know_the_plate')}
                </Checkbox>
              </Form.Item>
            </>
          )}

          {/* Vehicle data section */}
          <Divider orientation="left" style={{ fontSize: 13 }}>
            {translateX('vehicle_data')}
          </Divider>

          <Form.Item
            name="type"
            label={translateX('type')}
            rules={[{ required: true, message: () => translateX('required_field') }]}
            initialValue="car"
          >
            <Select options={VEHICLE_TYPE_OPTIONS} placeholder={translateX('select')} />
          </Form.Item>

          <Flex gap={16}>
            <Form.Item name="brand" label={translateX('brand')} style={{ flex: 1 }}>
              <Input
                placeholder={translateX('brand')}
                disabled={plateFound}
              />
            </Form.Item>

            <Form.Item name="model" label={translateX('model')} style={{ flex: 1 }}>
              <Input
                placeholder={translateX('model')}
                disabled={plateFound}
              />
            </Form.Item>
          </Flex>

          <Flex gap={16}>
            <Form.Item name="year" label={translateX('year')} style={{ flex: 1 }}>
              <Input
                placeholder="2024"
                maxLength={4}
                disabled={plateFound}
              />
            </Form.Item>

            <Form.Item name="color" label={translateX('color')} style={{ flex: 1 }}>
              <Input
                placeholder={translateX('color')}
                disabled={plateFound}
              />
            </Form.Item>
          </Flex>

          {/* Show plate field in edit mode (was already shown in add mode above) */}
          {editingVehicle && (
            <Form.Item name="plate" label={translateX('plate')}>
              <Input
                maxLength={PLATE_MAX_LENGTH}
                placeholder="ABC1234"
                style={{ textTransform: 'uppercase', letterSpacing: 2 }}
              />
            </Form.Item>
          )}

          <Flex gap={16}>
            <Form.Item name="fuel" label={translateX('fuel')} style={{ flex: 1 }}>
              <Input
                placeholder={translateX('fuel')}
                disabled={plateFound}
              />
            </Form.Item>

            <Form.Item name="tagNumber" label={translateX('tag_number')} style={{ flex: 1 }}>
              <Input placeholder={translateX('rfid_tag')} />
            </Form.Item>
          </Flex>

          <Form.Item name="observation" label={translateX('observation')}>
            <Input.TextArea rows={3} placeholder={translateX('observation')} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default VehiclesTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************

function mapSegmentToType(segment) {
  if (!segment) return 'car';
  const s = segment.toLowerCase();
  if (s.includes('moto') || s.includes('motorcycle')) return 'motorcycle';
  if (s.includes('caminh') || s.includes('truck')) return 'truck';
  if (s.includes('van') || s.includes('kombi')) return 'van';
  return 'car';
}
